import clsx from 'clsx';

import { tg } from '../../lib/tg';

export type StatusFilter = 'all' | 'pending' | 'done' | 'failed';
export type PeriodFilter = 'all' | '7d' | '30d';

interface Chip<T extends string> {
  value: T;
  label: string;
}

interface Props {
  status: StatusFilter;
  period: PeriodFilter;
  statusOptions: ReadonlyArray<Chip<StatusFilter>>;
  periodOptions: ReadonlyArray<Chip<PeriodFilter>>;
  onStatusChange: (s: StatusFilter) => void;
  onPeriodChange: (p: PeriodFilter) => void;
}

/** Compact horizontal filter rows for deposit / withdrawal history. */
export function HistoryFilters({
  status,
  period,
  statusOptions,
  periodOptions,
  onStatusChange,
  onPeriodChange,
}: Props) {
  return (
    <div className="space-y-2">
      <ChipRow
        options={statusOptions}
        value={status}
        onChange={(v) => onStatusChange(v as StatusFilter)}
      />
      <ChipRow
        options={periodOptions}
        value={period}
        onChange={(v) => onPeriodChange(v as PeriodFilter)}
      />
    </div>
  );
}

function ChipRow<T extends string>({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<Chip<T>>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="-mx-1 flex flex-wrap gap-1.5 px-1">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              tg.hapticLight();
              onChange(opt.value);
            }}
            className={clsx(
              'pb-focus-ring rounded-pb-sm border px-2.5 py-1 text-[12px] font-medium transition-colors',
              active
                ? 'border-pb-accent bg-pb-accent text-white'
                : 'border-pb-border-soft bg-pb-paper text-tg-hint hover:text-tg-text',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Apply chip filters to a list — date+status predicate factory. */
export function filterByStatusAndPeriod<
  T extends { status: string; created_at: string },
>(
  items: T[],
  status: StatusFilter,
  period: PeriodFilter,
  isDone: (s: string) => boolean,
  isFailed: (s: string) => boolean,
): T[] {
  const cutoff =
    period === '7d'
      ? Date.now() - 7 * 86400_000
      : period === '30d'
        ? Date.now() - 30 * 86400_000
        : 0;
  return items.filter((it) => {
    const ts = Date.parse(it.created_at);
    if (cutoff && (!Number.isFinite(ts) || ts < cutoff)) return false;
    if (status === 'all') return true;
    if (status === 'done') return isDone(it.status);
    if (status === 'failed') return isFailed(it.status);
    if (status === 'pending') return !isDone(it.status) && !isFailed(it.status);
    return true;
  });
}
